import { clsx } from 'clsx';
import { useField } from 'formik';
import { FC, useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import YupPassword from 'yup-password';

import { Icon } from 'src/general/Icons/Icon';
import { ButtonIcon } from '../../Button/ButtonIcon';
import { InputFormik } from '../Input/InputFormik';
import classes from './FormikInput.module.scss';
import { FormikInputTextProps } from './FormikInput.types';

type PasswordKey = 'minUppercase' | 'minLowercase' | 'minNumber' | 'min';

const errorMessages: { [key in PasswordKey]: string } = {
  min: 'Must contain at least 8 characters',
  minUppercase: 'Must contain at least 1 uppercase letter',
  minLowercase: 'Must contain at least 1 lowercase letter',
  minNumber: 'Must contain at least 1 number',
};

YupPassword(Yup); // extend yup

const FormikInputPasswordValidation = Yup.string()
  .min(8, ' ')
  .minLowercase(1, ' ')
  .minUppercase(1, ' ')
  .minNumbers(1, ' ')
  .required(' ');

interface PasswordMessage {
  key: PasswordKey;
  valid: boolean;
}

interface Props extends FormikInputTextProps {
  classNameWrapper?: string;
  withHelpText?: boolean;
  inputProps?: any;
  errorTestId?: string;
}

export const FormikInputPassword: FC<Props> = (props) => {
  const [field, meta] = useField(props.name);
  const [displayValidation, setDisplayValidation] = useState<boolean>(false);
  const [type, setType] = useState<'text' | 'password'>('password');

  const messages: PasswordMessage[] = useMemo(() => {
    const messages: PasswordMessage[] = [
      { key: 'minUppercase', valid: true },
      { key: 'minLowercase', valid: true },
      { key: 'minNumber', valid: true },
      { key: 'min', valid: true },
    ];
    try {
      FormikInputPasswordValidation.validateSync(field.value, { abortEarly: false });
    } catch (validationErrors: any) {
      if ('inner' in validationErrors) {
        (validationErrors as Yup.ValidationError).inner.forEach((error) => {
          const key = error.type;
          const messageIndex = messages.findIndex((item) => item.key === key);
          if (messageIndex >= 0) {
            messages[messageIndex].valid = false;
          }
        });
      }
    }
    return messages;
  }, [field.value]);

  const onFocus = useCallback(() => {
    if (!displayValidation) {
      setDisplayValidation(true);
    }
  }, [displayValidation]);

  const onClickViewPassword = () => {
    setType(type === 'text' ? 'password' : 'text');
  };

  return (
    <div className={clsx(classes.passwordWrapper, props.classNameWrapper)}>
      <InputFormik
        id="user-password"
        {...props}
        type={type}
        onFocus={onFocus}
        buttonRight={
          <ButtonIcon
            title="Toggle visibility"
            onClick={onClickViewPassword}
            iconName={type === 'text' ? 'hide' : 'show'}
            size="small"
            variant="tertiary"
            disabled={props.disabled}
            className={classes.viewPwdButton}
          />
        }
        className={classes.inputAction}
        errorTestId={props.errorTestId}
        data-testid="password-input"
      />
      {props.withHelpText && displayValidation && (
        <ul className={classes.passwordHelpText}>
          {messages.map(({ key, valid }) => (
            <li
              key={key}
              className={clsx(classes.passwordHelpTextItem, {
                [classes.passwordHelpTextValid]: valid,
                [classes.passwordHelpTextInvalid]: !valid && meta.touched,
              })}
            >
              {valid ? <Icon name="check" /> : <Icon name="close" />}
              <span className={classes.passwordHelpTextMessage}>{errorMessages[key]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
